import { ZERO } from "./bigUtil.ts";
import { CostType, costEntries } from "./CaseConfiguration.ts";
import { Case, SettlementCosts, SettlementOffer } from "./CaseModel.ts";
import { vatOn } from "./vat.ts";

/**
 * A side's settlement costs as stored before they had cost types: one amount,
 * of law firm fees.
 */
type StoredSettlementCosts = number | SettlementCosts;

const isStoredAsAmount = ({ costs }: SettlementOffer) =>
  typeof (costs.settlement.offeror as StoredSettlementCosts) === "number" ||
  typeof (costs.settlement.offeree as StoredSettlementCosts) === "number";

const typed = (stored: StoredSettlementCosts): SettlementCosts =>
  typeof stored !== "number"
    ? stored
    : stored > 0
      ? { lawFirmFee: stored }
      : {};

/** True while an offer of the case has settlement costs stored as one amount. */
export function needsSettlementCostTypes(
  c: Pick<Case, "settlements">,
): boolean {
  return (c.settlements ?? []).some(isStoredAsAmount);
}

/**
 * A case as the current code reads it: an offer's settlement costs stored as
 * one amount per side are that side's law firm fees, in memory here as every
 * save stores them.
 */
export function withSettlementCostTypes<C extends Pick<Case, "settlements">>(
  c: C,
): C {
  if (!needsSettlementCostTypes(c)) return c;
  return {
    ...c,
    settlements: c.settlements?.map((offer) =>
      isStoredAsAmount(offer)
        ? {
            ...offer,
            costs: {
              ...offer.costs,
              settlement: {
                offeror: typed(offer.costs.settlement.offeror),
                offeree: typed(offer.costs.settlement.offeree),
              },
            },
          }
        : offer,
    ),
  };
}

/** A side's settlement costs, type by type, those it has. */
export const settlementCostsByType = (
  costs: SettlementCosts,
): [CostType, number][] =>
  costEntries(costs).filter(([, amount]) => amount > 0);

/** What a side's settlement costs come to. */
export const settlementCostsTotal = (costs: SettlementCosts): number =>
  settlementCostsByType(costs)
    .reduce((total, [, amount]) => total.add(amount), ZERO)
    .toNumber();

/**
 * What a side's settlement costs come to as it pays them, with the VAT it
 * cannot reclaim (see paidWithVat): what they count as in a settlement.
 */
export const settlementCostsPaid = (
  costs: SettlementCosts,
  vatRate: number | undefined,
): number =>
  settlementCostsByType(costs)
    .reduce(
      (total, [type, amount]) =>
        total.add(amount).add(vatOn(amount, type, vatRate)),
      ZERO,
    )
    .toNumber();
